// Phase 2 money documents (06, 14): payments, tills, wallets, journals, reserves, approvals,
// and the notification outbox (08). All server-written.
import { z } from 'zod';
import { branchCodeSchema, sectorCodeSchema } from './config';

const minor = z.number().int().nonnegative().max(1e12);

/**
 * What a money line is tied to (14 §14.3): the real operational thing it belongs to. The server
 * checks that it exists, that the actor's scope covers it, and writes it on the money document and
 * on every journal line (dimensions `linkKind` and `linkId`), so a report can sum costs per thing.
 * One link per line, as in 5.0 (`linked_folder`).
 */
export const MONEY_LINK_KINDS = [
  'shipment',
  'container',
  'trip',
  'handover',
  'pickup',
  'branch',
  'partner',
] as const;
export type MoneyLinkKind = (typeof MONEY_LINK_KINDS)[number];

export const moneyLinkSchema = z
  .object({
    kind: z.enum(MONEY_LINK_KINDS),
    id: z.string().trim().min(1).max(60),
  })
  .strict();
export type MoneyLink = z.infer<typeof moneyLinkSchema>;

export const PAYMENT_METHODS = ['cash', 'card', 'bank', 'cheque', 'wallet'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * What a payment document can say. `voucher` is never sent by a client: the server makes that line
 * from a `voucherCode` (Dr 2020 or 4800, Cr 1100), so a client cannot name an amount for it.
 */
export const PAYMENT_DOC_METHODS = [...PAYMENT_METHODS, 'voucher'] as const;

/** `payments/{pay_…}` */
export const paymentDocSchema = z.object({
  id: z.string(),
  receiptNo: z.string(),
  direction: z.enum(['in', 'out']),
  method: z.enum(PAYMENT_DOC_METHODS),
  amountMinor: minor,
  currency: z.string(),
  /** The voucher a `voucher` payment used. */
  voucherCode: z.string().optional(),
  /** Money handed back so far, and the part of it that came out of customer credit (`refundPayment`). */
  refundedMinor: minor.optional(),
  refundedDepositMinor: minor.optional(),
  allocations: z.array(z.object({ shipmentId: z.string(), amountMinor: minor }).strict()),
  toDepositMinor: minor.default(0),
  reference: z.string().default(''),
  tillId: z.string().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  receivedBy: z.string(),
  journalId: z.string(),
  commandId: z.string(),
  at: z.string(),
});

/** `tills/{til_…}`: one cashier's session on a drawer (06 §6.4). */
export const tillDocSchema = z.object({
  id: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  cashierUid: z.string(),
  cashierName: z.string(),
  status: z.enum(['open', 'closed']),
  currency: z.string(),
  openingFloatMinor: minor,
  /** Cash in minus cash out recorded on this till, maintained by commands. */
  cashMovementMinor: z.number().int(),
  expectedMinor: z.number().int().optional(),
  countedMinor: minor.optional(),
  varianceMinor: z.number().int().optional(),
  openedAt: z.string(),
  closedAt: z.string().optional(),
});

/** `dayCloses/{branchId}__{date}` */
export const dayCloseDocSchema = z.object({
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  date: z.iso.date(),
  bookCashMinor: z.number().int(),
  countedMinor: minor,
  varianceMinor: z.number().int(),
  overrideReason: z.string().optional(),
  closedBy: z.string(),
  closedAt: z.string(),
});

export const WALLET_TYPES = [
  'till',
  'safe',
  'driver',
  'bank',
  'card',
  'reserve',
  'intercompany',
  'customer',
] as const;

/** `wallets/{wlt_…}` (14 §14.5). Balances come from the ledger, not from this document. */
export const walletDocSchema = z.object({
  id: z.string(),
  type: z.enum(WALLET_TYPES),
  name: z.string(),
  entityId: sectorCodeSchema,
  branchId: branchCodeSchema.optional(),
  accountCode: z.string(),
  currency: z.string(),
  active: z.boolean(),
});

/** Friendly categories for money out (14 §14.4). The account mapping lives in packages/domain. */
export const MONEY_OUT_CATEGORIES = [
  'freight',
  'telex_bl',
  'customs',
  'port_storage',
  'trucking',
  'forwarding',
  'delivery',
  'empty_boxes',
  'insurance',
  'rent',
  'salaries',
  'visas',
  'utilities',
  'fuel_vehicle',
  'marketing',
  'office',
  'bank_charges',
  'other',
] as const;
export type MoneyOutCategory = (typeof MONEY_OUT_CATEGORIES)[number];

export const journalLineSchema = z
  .object({
    accountCode: z.string().regex(/^\d{4}$/),
    debitMinor: minor,
    creditMinor: minor,
    dimensions: z.record(z.string(), z.string()).default({}),
  })
  .strict();

/** `journals/{entityId}__{sourceType}__{sourceId}__v{n}` (06 §6.1, 03 §3.9). Immutable. */
export const journalDocSchema = z.object({
  id: z.string(),
  entityId: sectorCodeSchema,
  currency: z.string(),
  businessDate: z.iso.date(),
  period: z.string().regex(/^\d{4}-\d{2}$/),
  sourceType: z.string(),
  sourceId: z.string(),
  version: z.number().int().positive(),
  reversalOf: z.string().optional(),
  memo: z.string(),
  lines: z.array(journalLineSchema).min(2),
  status: z.enum(['posted', 'reversed']),
  branchId: branchCodeSchema.optional(),
  commandId: z.string(),
  postedBy: z.string(),
  postedAt: z.string(),
  /**
   * Gap-free per entity and calendar year, stamped by the ledger when it applies the journal (06 §6.4).
   * `null` (or absent on older journals) until then.
   */
  journalNo: z.number().int().positive().nullable().optional(),
  /** `KSA-J-2026-0000123`, stamped with the number. */
  journalRef: z.string().nullable().optional(),
  /** Every account the lines touch, and every wallet they name: lets queries find journals by account or wallet. */
  accountCodes: z.array(z.string()).optional(),
  walletIds: z.array(z.string()).optional(),
});

export const RESERVE_BUCKETS = ['freight', 'telex_bl', 'destination', 'running'] as const;

/** `reserveAllocations/{shipmentId}__{bucket}` (14 §14.6). Memo only: no journals. */
export const reserveAllocationDocSchema = z.object({
  shipmentId: z.string(),
  bucket: z.enum(RESERVE_BUCKETS),
  entityId: sectorCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  currency: z.string(),
  allocatedMinor: minor,
  releasedMinor: minor,
  status: z.enum(['held', 'released', 'adjusted']),
  costModelId: z.string(),
  updatedAt: z.string(),
});

/** `approvals/{apv_…}` (14 §14.10) */
export const approvalDocSchema = z.object({
  id: z.string(),
  commandId: z.string(),
  kind: z.string(),
  /** For `kind: 'agentCommand'`: the held command's type (21 §21.1). */
  commandType: z.string().optional(),
  summary: z.string(),
  amountMinor: minor,
  currency: z.string(),
  makerUid: z.string(),
  requiredPermission: z.literal('finance.approve'),
  status: z.enum(['pending', 'approved', 'rejected']),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  checkerUid: z.string().optional(),
  note: z.string().optional(),
  createdAt: z.string(),
  decidedAt: z.string().optional(),
  /** What a money line posts if approved: shown to the checker so they can tally it (14 §14.10). */
  deferred: z
    .object({
      /** An agent's command held for a person: nothing is stored, the command itself runs on approval. */
      held: z.boolean().optional(),
      move: z
        .object({
          category: z.string().optional(),
          walletId: z.string().optional(),
          payee: z.string().optional(),
          payer: z.string().optional(),
          note: z.string().optional(),
        })
        .partial()
        .optional(),
      linkKind: z.string().nullable().optional(),
      linkId: z.string().nullable().optional(),
      receiptPath: z.string().nullable().optional(),
    })
    .partial()
    .optional(),
});

// ---- Notifications (08) ------------------------------------------------------

export const NOTIFICATION_TEMPLATES = [
  'shipment_created',
  'payment_received',
  'balance_due',
  'at_origin_hub',
  'at_hub',
  'exported',
  'eta_changed',
  'arrived_ph',
  'customs_hold',
  'customs_released',
  'handed_to_partner',
  'out_for_delivery',
  'delivered',
  'delivery_failed',
  'exception_notice',
  'season_risk',
  'feedback_request',
  'pickup_requested',
  'pickup_scheduled',
  'pickup_on_the_way',
  'pickup_failed',
  'arriving_soon',
  'partner_chase',
  'partner_manifest',
  'delivery_code',
  'otp_code',
  'approval_request',
  'alert',
  'daily_digest',
  // Phase 6 (08 §8.3, 15 §15.3–15.4). English and Tagalog text for every template: `@ecobox/domain` message-text.ts.
  'rate_delivery',
  'rate_service',
  'assistant_reply',
  'ticket_opened',
  'ticket_reply',
  'ticket_resolved',
  'ticket_followup',
  'shipment_shared',
  'pickup_cancelled',
  'pickup_rescheduled',
  // Phase 7 (04 §4.3, §4.6): a shipment was changed, cancelled, charged at the destination, or refunded.
  'shipment_amended',
  'shipment_cancelled',
  'destination_charge',
  'refund_issued',
] as const;
export type NotificationTemplate = (typeof NOTIFICATION_TEMPLATES)[number];

/** `notificationOutbox/{ntf_…}` */
export const notificationDocSchema = z.object({
  id: z.string(),
  template: z.enum(NOTIFICATION_TEMPLATES),
  channel: z.enum(['whatsapp', 'sms', 'push', 'email']),
  to: z.string(),
  role: z.enum(['shipper', 'consignee', 'shared', 'customer', 'partner', 'staff']),
  language: z.enum(['en', 'tl']).default('en'),
  params: z.record(z.string(), z.string()),
  shipmentId: z.string().optional(),
  /** Set on a message about a handover (the partner's manifest and chases). */
  handoverId: z.string().optional(),
  /**
   * True when part of the message is kept out of `params` on purpose (the delivery code). The
   * value lives in `outboxSecrets/{id}`, which no client can read (07 §7.3).
   */
  hasSecret: z.boolean().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  /** `suppressed`: not sent because the person said STOP, or has not opted in to marketing (08). */
  status: z.enum([
    'queued',
    'sending',
    'sent',
    'delivered',
    'read',
    'failed',
    'test_inbox',
    'suppressed',
  ]),
  /** How many times a send was tried (retries wait 1, 5, 15, then 60 minutes). */
  attempts: z.number().int().nonnegative().optional(),
  /** The message as the customer reads it (local and staging, rows with no secret part only). */
  text: z.string().optional(),
  ticketId: z.string().optional(),
  error: z.string().optional(),
  /** A held message (quiet hours, or a follow-up that waits a day): not sent before this time. */
  notBefore: z.string().optional(),
  /** The id the WhatsApp provider gave the message; delivery and read receipts find the row by it. */
  providerMessageId: z.string().optional(),
  /** Free text inside the customer's 24-hour window (a reply), not a template. */
  session: z.boolean().optional(),
  createdAt: z.string(),
  sentAt: z.string().optional(),
});

/**
 * `walletBalances/{walletId}`: kept up to date asynchronously from journals (06 §6.1), so screens
 * read one small document instead of adding up the ledger. The journals stay the truth.
 */
export const walletBalanceDocSchema = z.object({
  walletId: z.string(),
  entityId: sectorCodeSchema,
  branchId: branchCodeSchema.optional(),
  currency: z.string(),
  balanceMinor: z.number().int(),
  lastJournalId: z.string(),
  updatedAt: z.string(),
});

/** Wallet ids are predictable so commands can name them offline: `wlt_{BRANCH}_{type}` or `wlt_{ENTITY}_{type}_{n}`. */
export function branchWalletId(
  branchId: string,
  type: 'safe' | 'card' | 'driver' | 'reserve',
): string {
  return `wlt_${branchId}_${type}`;
}
export function tillWalletId(tillId: string): string {
  return `wlt_${tillId}`;
}
